import { RegisterCard } from "../components/RegisterCard";
import { TopAdminNav } from "../layouts/TopAdminNav";
import "../assets/css/main.css";
import "../assets/css/student.css";
import { useEffect, useState } from "react";
import BackButton from "../components/backButton";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import CustomModal from "../components/CustomModal/CustomModal";
import { Classroom } from "./Classes";
import { EditStudentForm } from "../layouts/editForms/student";
import { AddParentForm } from "../layouts/editForms/parent";
import toast from "react-hot-toast";
import { AddPickupForm } from "../layouts/editForms/emergencyContact";
import {
  formatTime,
  uploadImageToCloudinary,
  validateUploadFile,
} from "../utilities/importantFns";
import { generateAcademicSessions } from "../utilities/academicSession";
import { defaultUrl } from "../features/actions";
import { IPaginationParams } from "../types/interfaces";
import Pagination from "../components/pagination";
import {
  selectRe_RenderedState,
  setSharedState,
} from "../features/sharedStateSlices/shareState";
import {
  makeDeleteRequest,
  makeFetchRequest,
  makePostRequest,
  makePutRequest,
} from "../utilities/common";
// Expected Data Type Information
import { StudentDataInterface } from "../types/interfaces";
import StudentHeader from "../components/studentData/StudentHeader";
import { StudentTable } from "../components/studentData/StudentTable";
import { StudentFilter } from "../components/studentData/StudentFilter";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useDebounce } from "../hooks/useDebounce";

const STUDENTS_PER_PAGE = 10;

export const StudentData = () => {
  const dispatch = useDispatch();
  const reloaded = useSelector((state) => selectRe_RenderedState(state));

  /** DATA FETCHING */
  const [studentData, setStudentData] = useState<StudentDataInterface[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  // console.log(studentData)
  const [editModeType, setEditModeType] = useState<null | string>(null);
  const [editForm, setEditForm] = useState<StudentDataInterface>({ id: "" });
  const [emergencyTotal, setEmergencyTotal] = useState<object[]>([]);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [classroomId, setClassroomId] = useState("");
  const [academicSession, setAcademicSession] = useState<string | null>(null);

  const [editParentForm, setEditParentForm] = useState([
    {
      name: "",
      email: "",
      phone: "",
      home_number: "",
      work_number: "",
      relationship: "",
    },
  ]);
  const [editPickupForm, setEditPickupForm] = useState([
    {
      student_id: "",
      contact_name: "",
      phone: "",
      relationship: "",
      photo: "",
    },
  ]);

  // pagination parameters
  const [isLoading, setIsLoading] = useState(false);
  const [totalPage, setTotalPage] = useState(10);
  const [page, setPage] = useState(1);
  const [paginationParams, setPaginationParams] = useState<IPaginationParams>({
    page: 1,
    totalPages: 1,
    totalItems: 1,
    from: 1,
    to: 1,
  });

  /** FILTERINGS **/
  const [searchTerm, setSearchTerm] = useState("");
  const [classRoomFilter, setClassRoomFilter] = useState("");
  const [classRoomFilterId, setClassRoomFilterId] = useState("");

  const [filterByBirthday, setFilterByBirthday] = useState(false);
  const [isArchive] = useState(false);
  const debouncedSearchQuery: any = useDebounce(searchTerm, 500);

  const fetchClassrooms = async () => {
    const response = await makeFetchRequest(`${defaultUrl}/classroom`);
    setClassrooms(response);
    return response;
  };
  const handlePromote = async () => {
    const res = await makePutRequest(`${defaultUrl}/promote/students`, {
      students: "all",
      source_classroom_id: classRoomFilterId,
      promoted_to: classroomId,
      academic_session: academicSession,
    });
    if (res) {
      dispatch(setSharedState());
      setShowModal(false);
      setClassRoomFilterId("");
      setClassroomId("");
      setAcademicSession("");
    }
  };

  const getBirthdayKey = (dateValue?: string) => {
    if (!dateValue) return "";
    const [dateOnly] = dateValue.split(" ");
    const parts = dateOnly.split("-");

    if (parts.length >= 3) {
      return `${parts[1]}-${parts[2]}`;
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return "";
    return `${String(parsedDate.getMonth() + 1).padStart(2, "0")}-${String(
      parsedDate.getDate(),
    ).padStart(2, "0")}`;
  };

  const decorateStudents = (students: any[] = [], classroomList: any[] = []) =>
    students.map((student: any) => {
      const classroom =
        classroomList &&
        classroomList?.find((c: any) => {
          return c.id === student.classroom_id;
        });

      return {
        ...student,
        classroom,
        profile_picture: (
          <LazyLoadImage
            style={{ height: "100%", width: "100%" }}
            src={student.profile_picture}
            effect="blur"
            wrapperProps={{
              style: { transitionDelay: "1s" },
            }}
            alt="pupil"
          />
        ),
      };
    });

  const applyLocalFilters = (students: any[]) => {
    const todayBirthdayKey = getBirthdayKey(formatTime().split(" ")[0]);
    const searchValue = String(debouncedSearchQuery || "")
      .toLowerCase()
      .trim();

    return students
      .filter((data: any) => {
        if (!classRoomFilterId) return true;
        return String(data?.classroom_id) === String(classRoomFilterId);
      })
      .filter((data: any) => {
        if (!filterByBirthday) return true;
        return getBirthdayKey(data.date_of_birth) === todayBirthdayKey;
      })
      .filter((data: any) => {
        if (!searchValue) return true;
        return [data?.firstname, data?.surname, data?.othername, data?.email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(searchValue);
      });
  };

  const fetchStudentData = async (classrooms?: any) => {
    try {
      setIsLoading(true);
      const shouldPaginateLocally = Boolean(classRoomFilterId || filterByBirthday);
      const data = await makeFetchRequest(
        shouldPaginateLocally
          ? `${defaultUrl}/students/withoutPagination`
          : `${defaultUrl}/students?q=${debouncedSearchQuery}&page=${page}&per_page=${STUDENTS_PER_PAGE}`,
        !shouldPaginateLocally,
        true,
      );

      const rawStudents = shouldPaginateLocally ? data || [] : data?.data?.data || [];
      const sortedStudents = decorateStudents(rawStudents, classrooms || []).sort(
        function (a: any, b: any) {
          return (a?.firstname || "").localeCompare(b?.firstname || "");
        },
      );

      if (shouldPaginateLocally) {
        const filtered = applyLocalFilters(sortedStudents);
        const totalItems = filtered.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / STUDENTS_PER_PAGE));
        const currentPage = Math.min(page, totalPages);
        const fromIndex = totalItems ? (currentPage - 1) * STUDENTS_PER_PAGE : 0;
        const paginatedStudents = filtered.slice(
          fromIndex,
          fromIndex + STUDENTS_PER_PAGE,
        );

        if (currentPage !== page) {
          setPage(currentPage);
        }

        setPaginationParams({
          page: currentPage,
          totalPages,
          from: totalItems ? fromIndex + 1 : 0,
          to: fromIndex + paginatedStudents.length,
          totalItems,
        });
        setTotalPage(totalPages);
        setStudentData(paginatedStudents);
      } else {
        setPaginationParams({
          page: data?.data?.meta?.current_page || page,
          totalPages: data?.data?.meta?.last_page || 1,
          from: data?.data?.meta?.from || 0,
          to: data?.data?.meta?.to || 0,
          totalItems: data?.data?.meta?.total || 0,
        });
        setTotalPage(data?.data?.meta?.last_page || 1);
        setStudentData(sortedStudents);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      // console.log(error);
    }
  };

  useEffect(() => {
    fetchClassrooms().then((classes) => fetchStudentData(classes));
    // eslint-disable-next-line
  }, [reloaded]);

  useEffect(() => {
    fetchStudentData(classrooms);

    // eslint-disable-next-line
  }, [reloaded, debouncedSearchQuery, classRoomFilter, filterByBirthday, page]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, classRoomFilterId, filterByBirthday]);

  /** ACTIONS */

  // Edit Actions
  function handleInputChange({ target: { name, value, files } }: any) {
    // console.log({ name, value, files });
    if (files?.length > 0) {
      const validation = validateUploadFile(files[0], "image");
      if (!validation.valid) {
        toast.error(validation.message);
        return;
      }
    }
    setEditForm((prev) => ({
      ...prev,
      [name]: files?.length > 0 ? files[0] : value,
    }));
    return;
  }

  //console.log({ editForm: editForm?.profile_picture?.props?.src, studentData });
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSaveLoading(true);
    const formData = editForm;
    // console.log("form data", formData.profile_picture);

    // if media is available
    let profile_pictureString;
    if (
      typeof formData.profile_picture !== "string" &&
      formData.profile_picture.props === undefined
    ) {
      profile_pictureString = await uploadImageToCloudinary(
        formData.profile_picture,
      );
      formData.profile_picture = profile_pictureString || "";
    } else {
      profile_pictureString = formData.profile_picture.props.src;
      formData.profile_picture = profile_pictureString || "";
    }

    toast.remove();
    toast.loading("Saving profile...");

    if (editForm.parents && editForm.parents.length > 0) {
      try {
        const parentDataPayload = {
          data: editForm.parents.map((p: any) => ({
            parent_id: p.parent_id || p.id,
            name: p.name || "",
            phone: p.phone || "",
            home_number: p.home_number || "",
            work_number: p.work_number || "",
            relationship: p.relationship || "",
          })),
        };
        await makePutRequest(`${defaultUrl}/parents`, parentDataPayload);
      } catch (err) {
        console.error("Failed to update parent profile data:", err);
      }
    }

    const body = {
      ...formData,
      parent_id: editForm.parents?.map((p: any) => p.id || p.parent_id),
    };
    await makePutRequest(`${defaultUrl}/students/${editForm.id}`, body);

    toast.remove();
    fetchStudentData();
    setIsSaveLoading(false);
    setEditForm({ id: "" });
    setEditModeType(null);

    dispatch(setSharedState());
  };
  console.log(classRoomFilter);

  async function handleParentSubmit(e: any, existingParents: any) {
    e.preventDefault();
    toast.remove();
    let response: any;

    dispatch(setSharedState());

    // Create new Parent
    toast.loading("Saving profile, please wait...");

    // IF PARENT DOES NOT EXIST
    if (!existingParents) {
      let parentModified: Array<object> = [];

      // Add phone_number field to editParentForm
      editParentForm.map((item: any) => {
        const newParentObject = {
          name: item?.name,
          email: item?.email,
          phone: item?.phone,
          home_number: item?.home_number,
          work_number: item?.work_number,
          relationship: item?.relationship,
        };

        return parentModified.push(newParentObject);
      });

      // CREATE A PARENT
      response = await makePostRequest(`${defaultUrl}/parents/create`, {
        data: parentModified,
      })
        .then(async (res) => {
          const result = await res?.parent_id[0];
          if (result) {
            await linkParentToChild(result);
          } else {
            toast.error("Network Error, Try again");
          }
        })
        .catch((err: any) => {
          toast.remove();
          Swal.fire({
            title: `Error`,
            text: err?.response?.data?.message || "Network Error.",
            icon: "error",
            showCancelButton: false,
          });
        });
    } else {
      response = await existingParents;
      await linkParentToChild(response?.id);
    }
  }

  async function linkParentToChild(result: any) {
    toast.remove();
    toast.loading("Attaching Parent to Child...");

    if (result && editForm?.id) {
      await makePutRequest(`${defaultUrl}/parents/children/add`, {
        data: [
          {
            parent_id: result,
            children: [editForm?.id],
          },
        ],
      });

      toast.remove();
      toast.success("Parent attached successfully!");

      setEditParentForm([
        {
          name: "",
          email: "",
          phone: "",
          home_number: "",
          work_number: "",
          relationship: "",
        },
      ]);
      setEditModeType(null);

      // Immediately refresh shared state and refetch student list & details
      dispatch(setSharedState());
      const classes = await fetchClassrooms();
      await fetchStudentData(classes);
    }
  }

  async function handlePickupSubmit() {
    // Implement submit functionality
    // e.preventDefault();
    toast.remove();
    toast.loading("Creating Emergency contact, please wait...");
    // Check if emergency contact is equal to 2.
    if (emergencyTotal.length > 1) {
      toast.remove();
      return Swal.fire({
        title: "Ooops!",
        text: "Maximum number (2) of Emergency contacts reached, cannot add new contacts",
        icon: "error",
        confirmButtonText: "Close",
        allowOutsideClick: true,
      });
    } else {
      // start sending data
      editPickupForm.forEach(async (picker, i) => {
        let formData = { ...picker };

        let photo = await uploadImageToCloudinary(picker.photo);

        formData.photo = photo || "";
        formData.student_id = editForm.id;

        await makePostRequest(`${defaultUrl}/emergency/create`, formData);
        setEditPickupForm([
          {
            student_id: "",
            contact_name: "",
            phone: "",
            relationship: "",
            photo: "",
          },
        ]);
        setEditModeType(null);
        dispatch(setSharedState());
      });
    }
  }

  const handleParentInputChange = (
    index: number,
    { target: { name, value } }: any,
  ) => {
    setEditParentForm((parent: any) => {
      parent[index][name] = value;
      return [...parent];
    });
  };

  const handlePickupInputChange = (
    index: number,
    { target: { name, value, files } }: any,
  ) => {
    setEditPickupForm((person: any) => {
      if (files?.length > 0) {
        const validation = validateUploadFile(files[0], "image");
        if (!validation.valid) {
          toast.error(validation.message);
          return [...person];
        }
        person[index][name] = files[0];
      } else {
        person[index][name] = value;
      }
      return [...person];
    });
  };

  async function getEmergencyContact(wardID: string) {
    toast.loading("Checking for existing emergency contacts...");
    toast.remove();
    const response = await makeFetchRequest(
      `${defaultUrl}/emergency/student/${wardID}`,
    );
    setEmergencyTotal(response);
    setEditForm((pre) => ({ ...pre, emergencyContacts: response }));
    toast.remove();
    toast.success("Done!");
    return response?.length;
  }

  async function deleteEmergencyContact(id: string) {
    Swal.fire({
      title: `Confirm Action`,
      text: `Are you sure you want to delete this contact?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "red",
      confirmButtonText: "Delete",
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        makeDeleteRequest(`${defaultUrl}/emergency/delete/${id}`);
        getEmergencyContact(editForm.id);
        dispatch(setSharedState());
      }
    });
  }

  function handleArchieve(id: string) {
    Swal.fire({
      icon: "warning",
      title: "Archive Activity",
      text: "Are you sure you want to archive this pupil?",
      showCancelButton: true,
      confirmButtonColor: "green",
      confirmButtonText: "Yes, Archive",
      cancelButtonColor: "grey",
      cancelButtonText: "No, Cancel",
    }).then(async (response) => {
      if (response.isConfirmed) {
        await makePutRequest(`${defaultUrl}/archiveStudent/${id}`);
        dispatch(setSharedState());
      }
    });
  }

  return (
    <>
      <div className="w-full flex items-center">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Pupil Data" />
      </div>
      <div className="flex">
        <div className="block w-full">
          <div className="flex">
            <div className="w-full">
              <RegisterCard
                title="Pupil Management"
                details="Update on pupil"
              />
            </div>
          </div>
          <div className=" border border-gray-100 mt-7 attendance_bg ">
            <StudentFilter
              classrooms={classrooms}
              setClassRoomFilter={setClassRoomFilter}
              classRoomFilter={classRoomFilter}
              setClassRoomFilterId={setClassRoomFilterId}
              classRoomFilterId={classRoomFilterId}
              setSearchTerm={setSearchTerm}
              filterByBirthday={filterByBirthday}
              setFilterByBirthday={setFilterByBirthday}
            />
            {classRoomFilter && (
              <div className="flex justify-end items-end">
                <button
                  onClick={() => setShowModal(true)}
                  //disabled={!classroomId}
                  className="rounded-lg px-5  py-3 mr-5 bg-cyan-900 text-white font-semibold hover:bg-cyan-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Promote All
                </button>
              </div>
            )}

            <div className="text-[#012063CC] mt-1 p-4 px-2">
              <div className="w-full text-center overflow-x-auto">
                <div className="h-[60vh] w-[98%] !min-w-[700px] overflow-y-auto register-scroll">
                  <StudentTable
                    classrooms={classrooms}
                    studentData={studentData}
                    classRoomFilterId={classRoomFilterId}
                    isLoading={isLoading}
                    setEditForm={setEditForm}
                    setEditModeType={setEditModeType}
                    getEmergencyContact={getEmergencyContact}
                    handleArchieve={handleArchieve}
                    setClassRoomFilterId={setClassRoomFilterId}
                    editForm={editForm}
                  />
                  <Pagination
                    totalPages={totalPage}
                    page={page}
                    setPage={setPage}
                    paginationParams={paginationParams}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {editModeType === "child" && (
        <div className="">
          <CustomModal
            title={`Edit ${editForm?.firstname}'s Data`}
            width={65}
            children={
              <EditStudentForm
                form={editForm}
                classrooms={classrooms}
                handleChange={(e: any) => handleInputChange(e)}
                handleSubmit={handleSubmit}
                isLoading={isSaveLoading}
                onParentUnlinked={() => fetchStudentData(classrooms)}
              />
            }
            hideModal={() => {
              setEditModeType(null);
            }}
          />
        </div>
      )}
      {editModeType === "parent" && (
        <div className="">
          <CustomModal
            title={`Add a New Parent/Guardian to ${editForm.firstname}'s Profile `}
            width={60}
            children={
              <AddParentForm
                numberOfParents={editForm?.parents?.length}
                form={editParentForm}
                addParent={setEditParentForm}
                handleParentChange={(index: number, e: any) =>
                  handleParentInputChange(index, e)
                }
                handleParentSubmit={handleParentSubmit}
                editForm={editForm}
              />
            }
            hideModal={() => {
              setEditModeType(null);
              setEditParentForm([
                {
                  name: "",
                  email: "",
                  phone: "",
                  home_number: "",
                  work_number: "",
                  relationship: "",
                },
              ]);
            }}
          />
        </div>
      )}
      {editModeType === "pickup" && (
        <div className="">
          <CustomModal
            title={`Emergency Contacts`}
            width={60}
            children={
              <AddPickupForm
                deleteEmergencyContact={deleteEmergencyContact}
                pickups={editForm.emergencyContacts}
                numberOfPickups={editForm.emergencyContacts?.length || 0}
                form={editPickupForm}
                addPickup={setEditPickupForm}
                handlePickupChange={(index: number, e: any) =>
                  handlePickupInputChange(index, e)
                }
                handlePickupSubmit={handlePickupSubmit}
              />
            }
            hideModal={() => setEditModeType(null)}
          />
        </div>
      )}{" "}
      {showModal && (
        <div>
          <CustomModal
            title={`Promote all students from ${classRoomFilter} to another class`}
            width={30}
            children={
              <div className="flex flex-col gap-6 items-center p-4">
                {/* Dropdown */}
                <select
                  onChange={(e: any) => {
                    const { value } = e.target;

                    setClassroomId(value);
                  }}
                  value={classroomId || ""}
                  className="border border-gray-300 rounded-xl w-full max-w-xs p-3 text-center focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">Select a Classroom</option>
                  {classrooms?.map((classroom: any) => (
                    <option value={classroom.id} key={classroom.id}>
                      {classroom.name}
                    </option>
                  ))}
                </select>
                <select
                  onChange={(e: any) => {
                    const { value } = e.target;

                    setAcademicSession(value);
                  }}
                  value={academicSession || ""}
                  className="border border-gray-300 rounded-xl w-full max-w-xs p-3 text-center focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">Select a session</option>
                  {generateAcademicSessions(2025).map((s, i) => (
                    <option value={s.value} key={i}>
                      {s.label}
                    </option>
                  ))}
                </select>

                {/* Action Buttons */}
                <div className="flex gap-4 w-full max-w-xs">
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 border border-gray-300 rounded-lg py-2 hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePromote}
                    disabled={!classroomId}
                    className="flex-1 rounded-lg py-2 bg-cyan-900 text-white font-semibold hover:bg-cyan-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Promote
                  </button>
                </div>
              </div>
            }
            hideModal={() => setShowModal(false)}
          />
        </div>
      )}
    </>
  );
};
